import { apiGet, apiPost, apiPatch, apiDelete } from "./api";
import type {
  ApiResponse,
  ApiListResponse,
  UserProfile,
  WorkspaceWithMembership,
  Topic,
  Prompt,
  PromptDetail,
  Tag,
  CreatePromptBody,
  UpdatePromptBody,
  CreateTopicBody,
  UpdateTopicBody,
  CreateTagBody,
  UpdateTagBody,
} from "./types";

// ============================================================================
// User Profile
// ============================================================================

export const userService = {
  getMe: () =>
    apiGet<ApiResponse<UserProfile>>("/me").then((r) => r.data),

  updateMe: (body: { displayName?: string }) =>
    apiPatch<ApiResponse<UserProfile>>("/me", body).then((r) => r.data),
};

// ============================================================================
// Workspaces
// ============================================================================

const ws = (wId: string) => `/workspaces/${wId}`;

export const workspaceService = {
  list: () =>
    apiGet<ApiListResponse<WorkspaceWithMembership>>("/workspaces").then((r) => r.data),

  get: (workspaceId: string) =>
    apiGet<ApiResponse<WorkspaceWithMembership>>(ws(workspaceId)).then((r) => r.data),

  create: (body: { name: string; slug?: string }) =>
    apiPost<ApiResponse<{ id: string; name: string; slug: string }>>("/workspaces", body).then(
      (r) => r.data,
    ),

  update: (workspaceId: string, body: { name?: string; slug?: string }) =>
    apiPatch<ApiResponse<{ id: string }>>(ws(workspaceId), body).then((r) => r.data),

  delete: (workspaceId: string) =>
    apiDelete<ApiResponse<{ id: string; deleted: boolean }>>(ws(workspaceId)).then((r) => r.data),
};

// ============================================================================
// Topics
// ============================================================================

export const topicService = {
  list: (workspaceId: string) =>
    apiGet<ApiListResponse<Topic>>(`${ws(workspaceId)}/topics`).then((r) => r.data),

  create: (workspaceId: string, body: CreateTopicBody) =>
    apiPost<ApiResponse<Topic>>(`${ws(workspaceId)}/topics`, body).then((r) => r.data),

  update: (workspaceId: string, topicId: string, body: UpdateTopicBody) =>
    apiPatch<ApiResponse<Topic>>(`${ws(workspaceId)}/topics/${topicId}`, body).then(
      (r) => r.data,
    ),

  delete: (workspaceId: string, topicId: string) =>
    apiDelete<ApiResponse<{ id: string; deleted: boolean }>>(
      `${ws(workspaceId)}/topics/${topicId}`,
    ).then((r) => r.data),
};

// ============================================================================
// Prompts
// ============================================================================

export const promptService = {
  list: (
    workspaceId: string,
    params?: { search?: string; topicId?: string; page?: number; limit?: number },
  ) => {
    const query = new URLSearchParams();
    if (params?.search) query.set("search", params.search);
    if (params?.topicId) query.set("topicId", params.topicId);
    if (params?.page) query.set("page", String(params.page));
    if (params?.limit) query.set("limit", String(params.limit));
    const qs = query.toString();
    return apiGet<ApiListResponse<Prompt>>(
      `${ws(workspaceId)}/prompts${qs ? `?${qs}` : ""}`,
    ).then((r) => r.data);
  },

  get: (workspaceId: string, promptId: string) =>
    apiGet<ApiResponse<PromptDetail>>(`${ws(workspaceId)}/prompts/${promptId}`).then(
      (r) => r.data,
    ),

  create: (workspaceId: string, body: CreatePromptBody) =>
    apiPost<ApiResponse<{ id: string; slug: string; activeVersionId: string; versionNumber: number }>>(
      `${ws(workspaceId)}/prompts`,
      body,
    ).then((r) => r.data),

  update: (workspaceId: string, promptId: string, body: UpdatePromptBody) =>
    apiPatch<ApiResponse<{ id: string }>>(`${ws(workspaceId)}/prompts/${promptId}`, body).then(
      (r) => r.data,
    ),

  delete: (workspaceId: string, promptId: string, hard = false) =>
    apiDelete<ApiResponse<{ id: string; deleted: boolean; archived: boolean }>>(
      `${ws(workspaceId)}/prompts/${promptId}${hard ? "?hard=true" : ""}`,
    ).then((r) => r.data),

  setActiveVersion: (workspaceId: string, promptId: string, versionId: string) =>
    apiPatch<ApiResponse<{ id: string }>>(`${ws(workspaceId)}/prompts/${promptId}`, {
      activeVersionId: versionId,
    }).then((r) => r.data),
};

// ============================================================================
// Tags
// ============================================================================

export const tagService = {
  list: (workspaceId: string) =>
    apiGet<ApiListResponse<Tag>>(`${ws(workspaceId)}/tags`).then((r) => r.data),

  create: (workspaceId: string, body: CreateTagBody) =>
    apiPost<ApiResponse<Tag>>(`${ws(workspaceId)}/tags`, body).then((r) => r.data),

  update: (workspaceId: string, tagId: string, body: UpdateTagBody) =>
    apiPatch<ApiResponse<Tag>>(`${ws(workspaceId)}/tags/${tagId}`, body).then((r) => r.data),

  delete: (workspaceId: string, tagId: string) =>
    apiDelete<ApiResponse<{ id: string; deleted: boolean }>>(
      `${ws(workspaceId)}/tags/${tagId}`,
    ).then((r) => r.data),
};
